import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { PageHeader, StatusBadge } from "@/components/shared";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { fmtDate } from "@/lib/format";
import { ClipboardList, Search, Send, MessageSquare, ThumbsUp, Gauge, Inbox } from "lucide-react";

function Stat({ icon: Icon, label, value, tone = "#255058" }) {
  return (
    <Card className="p-4" data-testid={`ra-stat-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <div className="flex items-center gap-3">
        <div className="h-9 w-9 rounded-lg grid place-items-center" style={{ background: `${tone}15` }}>
          <Icon className="h-[18px] w-[18px]" style={{ color: tone }} />
        </div>
        <div>
          <div className="text-2xl font-display font-bold leading-none">{value}</div>
          <div className="text-xs text-muted-foreground mt-1">{label}</div>
        </div>
      </div>
    </Card>
  );
}

export default function RADashboard() {
  const navigate = useNavigate();
  const [d, setD] = useState(null);

  useEffect(() => {
    api.get("/ra/dashboard").then(({ data }) => setD(data)).catch((e) => toast.error(apiErr(e)));
  }, []);

  if (!d) return <div className="space-y-4"><Skeleton className="h-24" /><Skeleton className="h-64" /></div>;
  const p = d.performance;

  return (
    <div>
      <PageHeader title="R.A. Dashboard" subtitle="Your research, outreach and sourcing outcomes" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <Stat icon={ClipboardList} label="Active Titles" value={d.active_titles} />
        <Stat icon={Search} label="Research Leads" value={p.total_leads} />
        <Stat icon={Send} label="Outreach Sent" value={p.outreach} tone="#0ea5e9" />
        <Stat icon={MessageSquare} label="Replies" value={p.replies} tone="#8b5cf6" />
        <Stat icon={ThumbsUp} label="Positive Leads" value={p.positive_leads} tone="#10b981" />
        <Stat icon={Gauge} label="Reply Conversion" value={`${p.reply_conversion}%`} tone="#f59e0b" />
        <Stat icon={Gauge} label="Data Quality" value={`${p.data_quality}%`} tone="#10b981" />
        {d.is_admin && <Stat icon={Inbox} label="Awaiting Review" value={d.review_pending} tone="#ef4444" />}
      </div>

      {d.admin_overview && (
        <Card className="p-5 mb-4" data-testid="ra-admin-workflow-summary">
          <div className="font-display font-semibold mb-3">Admin Workflow Control</div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
            <WorkflowStat label="Pending review" value={d.admin_overview.review_pending} />
            <WorkflowStat label="Positive to assign" value={d.admin_overview.positive_unassigned} />
            <WorkflowStat label="BDM handoffs" value={d.admin_overview.bdm_assigned} />
            <WorkflowStat label="Returned" value={d.admin_overview.returned} />
            <WorkflowStat label="Overrides" value={d.admin_overview.duplicate_overrides} />
          </div>
        </Card>
      )}

      <Card className="p-5">
        <div className="font-display font-semibold mb-3">Recent Research Leads</div>
        {d.recent_leads.length ? (
          <div className="divide-y divide-border" data-testid="ra-dashboard-recent">
            {d.recent_leads.map((l) => (
              <div key={l.id} className="flex items-center gap-3 py-2.5 cursor-pointer hover:bg-accent/40 rounded-lg px-2"
                onClick={() => navigate("/ra/leads")}>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{l.company}</div>
                  <div className="text-xs text-muted-foreground">{l.position_title} · {l.location || "—"}</div>
                </div>
                <StatusBadge value={l.status} />
                <div className="text-xs text-muted-foreground w-24 text-right">{fmtDate(l.updated_at)}</div>
              </div>
            ))}
          </div>
        ) : <div className="text-sm text-muted-foreground">No research leads yet. Start from My Assigned Titles.</div>}
      </Card>
    </div>
  );
}

function WorkflowStat({ label, value }) {
  return (
    <div className="border-l-2 border-[#255058]/30 pl-3" data-testid={`ra-admin-${label.replace(/\s+/g, "-")}`}>
      <div className="font-display text-xl font-bold">{value}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </div>
  );
}
